"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useId, type ReactNode } from "react";
import { PHRASE_CHIP_ROW_PX, SHELL_INSET_PX } from "./panel-metrics";
import { publishShellOccupancy } from "./shell-occupancy";

/**
 * Three shapes for one panel.
 *
 * `band` is the panel reduced to a line across the top of the roll. Once the
 * player is playing, the roll is the screen and the panel has one thing to
 * say. It spans the width rather than sitting in a column, because a band
 * that dodges left and right while the player reads would be a moving object
 * in the corner of their eye. It sits at the **top**: the lane between the
 * playhead and the keys is where a note visually falls onto the key it
 * lights, and that fall is the link between the notation and the keyboard
 * on a product that shows no score.
 *
 * `floating` is the capsule shown whenever the player is **not** playing:
 * before a pass starts, and once it has ended. Every screen where the player
 * plays on the roll uses the band, so by construction nobody has to read the
 * roll behind a capsule. It therefore sits in the **middle** of the roll,
 * under the row the phrase chip keeps for itself (`PHRASE_CHIP_ROW_PX`),
 * and is never capped or scrolled: the panel is the screen at that moment,
 * and a scrollbar on a panel that has nothing to dodge was the bug. This
 * used to be a column that picked the side leaving the highlighted notes
 * visible, height-capped to keep a reading corridor above the playhead when
 * neither side was free; that cap is what turned the panel into a scrolling
 * sliver on short rolls, for notes nobody needed to see at that point.
 *
 * `sidebar` is the harmony parcours' column, pinned to the right edge,
 * top to bottom. It is the one shape that still scrolls: it is as tall as
 * the roll, so on a short viewport its content would otherwise be clipped
 * with no way to reach the primary action.
 */
type Variant = "sidebar" | "floating" | "band";

type FloatingShellProps = {
  readonly variant: Variant;
  readonly width?: number;
  readonly children: ReactNode;
  readonly ariaLabel: string;
  readonly titleId?: string;
  /**
   * Stable identity used by framer-motion to bridge layout animations
   * between two mount/unmount cycles. Default: shared across the whole
   * `/learn-piece` parcours so S0 (sidebar) → S1a (floating capsule)
   * morphs smoothly even though distinct React components mount.
   */
  readonly layoutId?: string;
  /** Stable key for the inner content; drives the crossfade. */
  readonly contentKey?: string;
};

const SHARED_LAYOUT_ID = "learn-piece-shell";
const SIDEBAR_WIDTH_PX = 400;
const FLOATING_WIDTH_PX = 360;

export function FloatingShell({
  variant,
  width,
  children,
  ariaLabel,
  titleId,
  layoutId = SHARED_LAYOUT_ID,
  contentKey,
}: FloatingShellProps) {
  const fallbackId = useId();
  const labelledById = titleId ?? fallbackId;
  const reducedMotion = useReducedMotion();

  const floatingWidth = width ?? FLOATING_WIDTH_PX;

  // La place prise est publiée pour les voisins qui doivent s'en écarter —
  // la puce de phrase en haut, l'invitation à donner un retour en bas. Un
  // effet plutôt qu'un rendu : écrire dans un magasin externe est ce à quoi
  // il sert, et ça évite le rendu en cascade qu'un `setState` remontant
  // produirait à chaque étape.
  useEffect(() => {
    publishShellOccupancy(
      variant === "band"
        ? { kind: "band" }
        : variant === "sidebar"
          ? { kind: "column", width: SIDEBAR_WIDTH_PX }
          : { kind: "centered", width: floatingWidth },
    );

    return () => {
      publishShellOccupancy(null);
    };
  }, [variant, floatingWidth]);

  const glass: React.CSSProperties = {
    background: "var(--color-glass-shell)",
    backdropFilter: "blur(var(--blur-shell)) saturate(140%)",
    WebkitBackdropFilter: "blur(var(--blur-shell)) saturate(140%)",
    borderRadius: "var(--radius-shell)",
    boxShadow: "var(--shadow-popin-focus)",
    border: "1px solid var(--color-border-subtle)",
    // The box is as tall as its content, so nothing is ever clipped; this
    // only keeps children inside the rounded corners.
    overflow: "hidden",
  };

  const baseStyle: React.CSSProperties =
    variant === "band"
      ? {
          position: "absolute",
          top: SHELL_INSET_PX,
          left: SHELL_INSET_PX,
          right: SHELL_INSET_PX,
          ...glass,
        }
      : variant === "sidebar"
        ? {
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            width: SIDEBAR_WIDTH_PX,
            borderLeft: "1px solid var(--color-border-subtle)",
            background:
              "linear-gradient(180deg, var(--color-glass-rail-top) 0%, var(--color-glass-rail-bottom) 100%)",
            backdropFilter: "blur(var(--blur-rail)) saturate(140%)",
            WebkitBackdropFilter: "blur(var(--blur-rail)) saturate(140%)",
            overflowX: "hidden",
            overflowY: "auto",
          }
        : {
            // Centred with `left: 0; right: 0; margin-inline: auto` rather
            // than a translate: framer-motion's `layout` animation owns the
            // transform, and a translate of our own would fight it.
            position: "absolute",
            top: SHELL_INSET_PX + PHRASE_CHIP_ROW_PX,
            left: 0,
            right: 0,
            marginInline: "auto",
            width: floatingWidth,
            ...glass,
          };

  const transition = reducedMotion
    ? { duration: 0 }
    : { type: "spring" as const, stiffness: 280, damping: 32, mass: 0.9 };

  const contentTransition = reducedMotion
    ? { duration: 0 }
    : { duration: 0.2, ease: "easeOut" as const };

  return (
    <motion.div
      layout
      layoutId={layoutId}
      transition={transition}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-label={titleId ? undefined : ariaLabel}
      style={{ ...baseStyle, zIndex: 30, pointerEvents: "auto" }}
      data-shell-variant={variant}
      data-shell-titleid={labelledById}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={contentKey ?? variant}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={contentTransition}
          // `minHeight` (not `height`) so the sidebar's content still fills
          // it when short, and grows past it — and scrolls — when it isn't.
          style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
